import React, { useState, useEffect, useRef } from 'react'

import { Box, Skeleton, styled, useTheme, type SxProps, type Theme, type BoxProps } from '@mui/material'

import { ProductImagePlaceholder } from '../ProductImagePlaceholder'

interface ImageProps {
  src: string
  alt?: string
  fit?: Fit
  sx?: SxProps<Theme>
  onLoad?: () => void
  onError?: () => void
}

type Fit = 'cover' | 'contain'

type Status = 'loading' | 'loaded' | 'failed'

// Only show the skeleton if the bitmap hasn't decoded within this window. Below the
// ~100-200ms "feels instant" perceptual threshold, so fast/cached images (the common
// case once a page has loaded once) fade straight in with no skeleton flash; only
// genuinely slow loads — the case a skeleton is actually for — ever show one.
const SKELETON_DELAY_MS = 150

const ImageComponent = (props: ImageProps) => {
  const { src, alt, fit = 'contain', sx, onLoad, onError } = props
  const [status, setStatus] = useState<Status>(src ? 'loading' : 'failed')
  const [showSkeleton, setShowSkeleton] = useState(false)
  // A cached image is already on screen — show it INSTANTLY (no fade) so re-visits
  // don't even blink; only genuinely-loading images get the fade-in.
  const [instant, setInstant] = useState(false)
  const theme = useTheme()
  const imgRef = useRef<HTMLImageElement>(null)

  // Reset to loading whenever the source changes so the skeleton + fade replay for
  // the new image instead of leaving the previous one (or a stale state) on screen.
  // The skeleton is armed on a delay so it never flashes for a fast load.
  useEffect(() => {
    setStatus(src ? 'loading' : 'failed')
    setShowSkeleton(false)
    setInstant(false)
    if (!src) return
    // A CACHED image is already decoded when the <img> mounts, and the browser won't
    // re-fire `onLoad` for it — so without this it stays 'loading', the delayed
    // skeleton fires, and re-visiting a page flashes grey over an image that's already
    // there. Detect completeness after paint (ref attached) and mark it loaded at once.
    const raf = requestAnimationFrame(() => {
      const el = imgRef.current
      if (el && el.complete && el.naturalWidth > 0) {
        setInstant(true)
        setStatus('loaded')
      }
    })
    const timer = setTimeout(() => setShowSkeleton(true), SKELETON_DELAY_MS)
    return () => {
      clearTimeout(timer)
      cancelAnimationFrame(raf)
    }
  }, [src])

  if (status === 'failed' || !src) {
    /*
     * The truly-missing-image state, now drawn by the SHARED
     * `ProductImagePlaceholder` rather than a local grey plate plus a generic
     * photo glyph.
     *
     * It was `grey[200]` — 82% lightness, which against a white surface is a
     * solid block rather than an absence, so a table of un-photographed products
     * read as a column of slabs. The placeholder carries the lighter plate and a
     * product glyph; see its docstring for why a shopping bag rather than a
     * picture-frame icon.
     *
     * `sx` is still applied AFTER, so callers keep their overrides (radius,
     * border, size) on matching keys. This must NOT apply once an image has
     * loaded — an asset with its own transparency should show through to the
     * page rather than sit on a forced backdrop.
     */
    return (
      <StyledImageWrapper sx={sx} fit={fit}>
        <ProductImagePlaceholder />
      </StyledImageWrapper>
    )
  }

  return (
    <StyledImageWrapper sx={sx} fit={fit}>
      {/* Skeleton fills the image's footprint while the bitmap loads, so each image
          fades in smoothly in place instead of popping in from blank — and at random
          times — as its individual request resolves on page load. */}
      {status === 'loading' && showSkeleton && <Skeleton variant="rounded" animation="wave" />}

      {/* The real <img> is always mounted so the browser actually loads it; its own
          onLoad (a DOM event, not a throttled requestAnimationFrame) drives the fade,
          so images never get stuck invisible in a backgrounded/headless tab. */}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        onLoad={() => {
          setStatus('loaded')
          onLoad?.()
        }}
        onError={() => {
          setStatus('failed')
          onError?.()
        }}
        style={{
          opacity: status === 'loaded' ? 1 : 0,
          // Cached images (instant) skip the fade so re-visits don't blink; freshly
          // loading images still fade in smoothly.
          transition: instant ? 'none' : 'opacity 250ms ease-in-out',
          borderRadius: 8,
        }}
      />
    </StyledImageWrapper>
  )
}

type WrapperProps = BoxProps & {
  fit?: Fit
}

const StyledImageWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'fit',
})<WrapperProps>(({ fit }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  textAlign: 'center',
  borderRadius: 8,

  img: {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    height: '100%',
    objectFit: fit,
    borderRadius: 8
  },

  '.MuiSkeleton-root': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    transform: 'none',
    borderRadius: 8
  },
}))

export default ImageComponent
